import { useCallback, useEffect, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAvailabilityResourceMonth } from "@/hooks/use-availability";
import type { GroupCellResult } from "@/lib/availability/groups";
import type { AvailabilityCellPayload } from "@/schemas/availability";
import { formatHours, formatNetWords, fullMonthLabel, STATE_LABEL } from "./labels";
import { type GridGroup, groupMonthTotals, type ViewRow } from "./model";

/** What a hovered or focused cell is about. */
export type TipTarget =
  | { kind: "resource"; row: ViewRow; cell: AvailabilityCellPayload }
  | { kind: "group"; group: GridGroup; month: string; result: GroupCellResult };

interface OpenTip {
  target: TipTarget;
  anchor: DOMRect;
}

/** The pause before a hover shows the tooltip. Keyboard focus shows it at once. */
export const TOOLTIP_DELAY_MS = 300;

export const TOOLTIP_ID = "availability-cell-tooltip";

/**
 * One tooltip for the whole grid, not one per cell. A hover opens it after 300 ms;
 * keyboard focus opens it immediately. Scrolling closes it (its anchor has moved).
 */
export function useGridTooltip() {
  const [tip, setTip] = useState<OpenTip | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    setTip(null);
  }, []);

  const show = useCallback((target: TipTarget, element: HTMLElement, immediate: boolean) => {
    clearTimeout(timer.current);
    const open = () => setTip({ target, anchor: element.getBoundingClientRect() });
    if (immediate) open();
    else timer.current = setTimeout(open, TOOLTIP_DELAY_MS);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", hide, true);
    return () => {
      window.removeEventListener("scroll", hide, true);
      clearTimeout(timer.current);
    };
  }, [hide]);

  return { tip, show, hide };
}

const TIP_WIDTH_PX = 288;
const TIP_GAP_PX = 6;
const TIP_HEIGHT_GUESS_PX = 150;

function placement(anchor: DOMRect): { left: number; top?: number; bottom?: number } {
  const margin = 8;
  const centre = anchor.left + anchor.width / 2;
  const left = Math.max(
    margin,
    Math.min(centre - TIP_WIDTH_PX / 2, window.innerWidth - TIP_WIDTH_PX - margin),
  );
  if (anchor.bottom + TIP_GAP_PX + TIP_HEIGHT_GUESS_PX > window.innerHeight) {
    return { left, bottom: window.innerHeight - anchor.top + TIP_GAP_PX };
  }
  return { left, top: anchor.bottom + TIP_GAP_PX };
}

export function CellTooltip({ tip }: { tip: OpenTip }) {
  const { left, top, bottom } = placement(tip.anchor);
  return (
    <div
      role="tooltip"
      id={TOOLTIP_ID}
      className="pointer-events-none fixed z-50 flex flex-col gap-1 rounded-md bg-sidebar px-3.5 py-3 text-xs leading-snug text-sidebar-foreground shadow-lg"
      style={{ left, top, bottom, width: TIP_WIDTH_PX }}
    >
      {tip.target.kind === "resource" ? (
        <ResourceTip row={tip.target.row} cell={tip.target.cell} />
      ) : (
        <GroupTip group={tip.target.group} month={tip.target.month} result={tip.target.result} />
      )}
    </div>
  );
}

/** The state line: signed net + state, or the reason there is no number. */
function stateLine(cell: AvailabilityCellPayload): string {
  switch (cell.state) {
    case "noData":
      return "No data";
    case "unplanned":
      return "Unplanned: past the plan horizon with no Guidance. Unknown, not free.";
    case "overUtilized":
    case "stretched":
      return `+${formatHours(-cell.netHours)} h · ${STATE_LABEL[cell.state]}`;
    default:
      return `${formatNetWords(cell.netHours)} · ${STATE_LABEL[cell.state]}`;
  }
}

function ResourceTip({ row, cell }: { row: ViewRow; cell: AvailabilityCellPayload }) {
  const wantsSolutions = cell.activeHours + cell.proposedHours > 0;
  const { data, isLoading, isError } = useAvailabilityResourceMonth(
    row.resourceId,
    cell.month,
    wantsSolutions,
  );
  const top = data?.solutions.slice(0, 3) ?? [];
  const timeOff = cell.timeOffHours ?? 0;

  return (
    <>
      <span className="font-semibold">
        {row.name} · {fullMonthLabel(cell.month)}
      </span>
      {cell.availableHours === null ? (
        <span>No Available Hours on record this month.</span>
      ) : (
        <span>
          Available {formatHours(cell.availableHours)} h · Booked {formatHours(cell.bookedHours)} h
          {cell.hasProposed ? ` (${formatHours(cell.proposedHours)} proposed)` : ""}
        </span>
      )}
      <span className="font-semibold">{stateLine(cell)}</span>
      {wantsSolutions ? (
        isLoading ? (
          <Skeleton className="h-4 w-full bg-sidebar-foreground/20" />
        ) : isError ? (
          <span className="opacity-70">Solutions could not be loaded.</span>
        ) : top.length > 0 ? (
          <span>
            {top
              .map(
                (s) =>
                  `${s.name}${s.podPosition ? ` ${s.podPosition}` : ""} ${formatHours(s.hours)}`,
              )
              .join(" · ")}
          </span>
        ) : null
      ) : null}
      {timeOff > 0 ? <span>Time off {formatHours(timeOff)} h</span> : null}
    </>
  );
}

function GroupTip({
  group,
  month,
  result,
}: {
  group: GridGroup;
  month: string;
  result: GroupCellResult;
}) {
  const totals = groupMonthTotals(group.members, month);
  return (
    <>
      <span className="font-semibold">
        {group.label} · {fullMonthLabel(month)}
      </span>
      {totals.planned > 0 ? (
        <span>
          {totals.planned} of {totals.total} with a plan · Available{" "}
          {formatHours(totals.availableHours)} h · Booked {formatHours(totals.bookedHours)} h
        </span>
      ) : (
        <span>No plan this month.</span>
      )}
      <span className="font-semibold">
        {result.state === "noData" || result.state === "unplanned"
          ? STATE_LABEL[result.state]
          : `${formatNetWords(result.netHours)} · ${STATE_LABEL[result.state]}`}
      </span>
      <span>
        {totals.overCount} over · {totals.roomCount} with room
      </span>
    </>
  );
}
